import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'
import { dialog, i18N, toast } from '@/common/util'
import cursosService from '@/services/cursos.service'
import { courseService } from '@/services/cursos.service'

import type { CursoType, InscreverUsuarios, LoaderType } from '@/types/types'

const defaultStatus: Record<string, LoaderType> = {
  create: 'initial',
  update: 'initial',
  delete: 'initial',
  getById: 'initial',
  getAll: 'initial',
  associations: 'initial',
  subscriptions: 'initial',
  favorites: 'initial',
  reaction: 'initial'
}

export const useCursosStore = defineStore('cursos', () => {
  /**
   * States
   */
  const list = ref<CursoType[]>([])
  const status = reactive<typeof defaultStatus>({...defaultStatus})

  /**
   * Getters
   */
  const loading = computed(() => Object.values(status).some(el => el === 'loading'))

  /**
   * Actions
   */
  const create = async (objData: CursoType) => {
    status.create = 'loading'

    return cursosService.create<CursoType>(objData)
      .then((response) => {
        const { data, success } = response

        status.create = success ? 'success' : 'error'
        if (success)
          getAll()

        return data
      })
      .catch((e) => {
        status.create = 'error'
        throw e
      })
  }

  const update = async (objData: CursoType, id: string) => {
    status.update = 'loading'

    return cursosService.update<CursoType>(id, objData)
      .then((response) => {
        const { data, success } = response

        status.update = success ? 'success' : 'error'
        if (data?.id) {
          const value = list.value.find(el => el.id === data.id)
          if (value) Object.assign(value, data)
        }

        return data
      })
      .catch((e) => {
        status.update = 'error'
        throw e
      })
  }

  const clear = () => {
    list.value.splice(0, list.value.length)
    Object.assign(status, {...defaultStatus})
  }

  const getAll = async (): Promise<void> => {
    status.getAll = 'loading'

    try {
      const { data } = await cursosService.get()
      status.getAll = 'success'
      list.value = data
    } catch {
      status.getAll = 'error'
    }
  }

  const remove = async (curso: CursoType, force?: boolean) => {
    if (!force)
      return dialog.open({
        title: i18N('feedback.attention'),
        message: i18N('dialogs.delete.message', {type: i18N('labels.course').toLowerCase(), name: curso.titulo}),
        complete: () => remove(curso, true)
      })

    try {
      status.delete = 'loading'
      const { success } = await cursosService.remove(curso.id)
      if (success) list.value.splice(list.value.findIndex(el => el.id === curso.id), 1)
      status.delete = 'success'
      return success
    } catch {
      status.delete = 'error'
    }
  }

  const getById = async (id: string): Promise<CursoType> => {
    try {
      status.getById = 'loading'
      const { data } = await cursosService.get(id)
      status.getById = 'success'
      return data as CursoType
    } catch {
      status.getById = 'error'
    }
  }

  const exportCursos = async () => {
    try {
      return await cursosService.exportCsv()
    } catch {
      //
    }
  }

  const getAssociations = async (cursoId: string) => {
    status.associations = 'loading'
    try {
      const { data } = await cursosService.getBonds(cursoId)
      status.associations = 'success'
      return data
    } catch {
      status.associations = 'error'
    }
  }

  const sendSubscriptions = async (data: InscreverUsuarios, inscrever?: boolean): Promise<void> => {
    status.subscriptions = 'loading'
    try {
      const {success} = await cursosService.sendSubscriptions(data, inscrever)
      if(success) {
        toast.success({
          message: inscrever
          ? i18N('toasts.inscriptions.subscribe', [data.usuariosIds.length])
          : i18N('toasts.inscriptions.unsubscribe', [data.usuariosIds.length])
        })
        status.subscriptions = 'success'
      }
    } catch {
      status.subscriptions = 'error'

      throw new Error('Erro ao inscrever alunos!')
    }
  }

  const setSubscription = async(id: string, unsubscribe?: boolean) => {
    status.subscriptions = 'loading'

    try {
      const { success } = (!unsubscribe ? await courseService.enroll(id) : await courseService.unenroll(id))
      status.subscriptions = 'success'

      return success
    } catch {
      status.subscriptions = 'error'
    }
  }

  const setFavorite = async(id: string, favorite?: boolean) => {
    status.favorites = 'loading'

    try {
      const { success } = (favorite ? await courseService.bookmark(id) : await courseService.unbookmark(id))
      status.favorites = 'success'

      return success
    } catch {
      status.favorites = 'error'
    }
  }

  const setReactionCourse = async (objData: { id: string, reacao?: string }) => {
    status.reaction = 'loading'

    try {
      const data = await courseService.setReactionCourse(objData)
      status.reaction = 'success'

      return data
    } catch {
      status.reaction = 'error'
    }
  }

  return {
    list,
    status,
    // Getters
    loading,
    // Actions
    create,
    update,
    clear,
    getAll,
    getById,
    remove,
    exportCursos,
    getAssociations,
    sendSubscriptions,
    setSubscription,
    setFavorite,
    setReactionCourse
  }
})

export default useCursosStore
